import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Paper, Button, Group, Text, Alert, Title, Divider, SimpleGrid } from '@mantine/core';
import { IconPlus, IconSparkles } from '@tabler/icons-react';
import { PrimaryButton, SecondaryButton } from '../../components/UI';
import VariationCard from '../../components/VariationCard';
import EnsembleCard, { EnsembleJoinForm } from '../../components/EnsembleCard';
import { getAgeDivision } from '../../services/registrationService';
import * as registrationService from '../../services/registrationService';
import { isAdultDivision, COMPETITIVE_CATEGORY, softShoeWarning } from '../../config/categories';
import { postApi } from '../../lib/api';

const MAX_SOLOS = 2;

const SOLO_FIELDS = [
  'performance_category', 'footwear', 'variation_name', 'ballet_origin', 'character_name',
  'variation_length', 'start_music', 'music_file_url', 'music_file_name',
];
const EMPTY_SOLO = Object.fromEntries(SOLO_FIELDS.map(f => [f, '']));

const pickSoloFields = (row) => Object.fromEntries(SOLO_FIELDS.map(f => [f, row[f] || '']));

// The form keeps '' for "not answered yet"; these columns only accept their
// listed values or null, so an unanswered one is saved as null.
const NULL_WHEN_EMPTY = ['start_music', 'footwear'];
const toRow = (data) => ({
  ...data,
  ...Object.fromEntries(NULL_WHEN_EMPTY.map(f => [f, data[f] || null])),
});

const money = (n) => `$${Number(n).toFixed(Number.isInteger(Number(n)) ? 0 : 2)}`;
const shortDate = (iso) => new Date(iso).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', timeZone: 'America/Toronto' });

// "Solos  1 of 2 ............ [+ Add a solo · $160]"
const SectionHeader = ({ title, count, max, action }) => (
  <Group justify="space-between" align="center" mt="xl" mb="sm" wrap="wrap" gap="xs">
    <Group gap="xs" align="baseline">
      <Title order={3} size="h4" style={{ margin: 0, color: 'var(--charcoal)' }}>{title}</Title>
      {max != null && <Text size="sm" color="dimmed">{count} of {max}</Text>}
    </Group>
    {action}
  </Group>
);

const AddButton = ({ children, ...props }) => (
  <Button size="sm" variant="subtle" color="gold" leftSection={<IconPlus size={16} />} {...props}>
    {children}
  </Button>
);

/**
 * Step 3: My Entries.
 *
 * Up to 2 solos (each in a variation slot, 1 or 2) and, once teams ship,
 * up to 2 ensembles. At least one entry is needed to continue. Entries are
 * paid for at checkout; a dancer can come back, add more, and pay the
 * difference until registration closes. Paid entries stay editable but can't
 * be removed here (refunds go through the office).
 */
const Step3Variations = ({
  registration,
  dancerInfo,
  variations,
  saveVariation,
  saving,
  goToNextStep,
  goToPreviousStep,
  refreshRegistration,
  updateRegistrationData,
  readOnly = false
}) => {
  const navigate = useNavigate();
  const [ageDivision, setAgeDivision] = useState('');
  // [{ number, data, persisted, paid }] — one per solo card, ordered by slot.
  const [solos, setSolos] = useState([]);
  const [errors, setErrors] = useState({});
  // Which solo cards are open. Unset = saved cards closed, new cards open.
  const [expanded, setExpanded] = useState({});
  const isOpen = (solo) => expanded[solo.number] ?? !solo.persisted;
  const toggle = (number, open) => setExpanded(prev => ({ ...prev, [number]: open }));
  const [removing, setRemoving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // Unit prices and what's already paid, from /api/price-quote. Labels only;
  // totals are shown at checkout.
  const [prices, setPrices] = useState(null);
  const [paidInfo, setPaidInfo] = useState(null);

  // Ensemble entries and their teams, from /api/ensemble (the browser can't
  // read those tables directly). Every action returns the fresh list.
  const location = useLocation();
  // A team invite link (?team=PDC-7KQ3) is remembered in this browser until
  // the dancer joins or cancels, so it survives signing up (email confirmation
  // opens a new tab) and finishing Step 1 first.
  const INVITE_KEY = 'pdc_team_invite';
  const urlInvite = new URLSearchParams(location.search).get('team') || '';
  const inviteCode = urlInvite || (() => { try { return localStorage.getItem(INVITE_KEY) || ''; } catch { return ''; } })();
  useEffect(() => {
    if (!urlInvite) return;
    try { localStorage.setItem(INVITE_KEY, urlInvite); } catch { /* private mode: the URL still works */ }
  }, [urlInvite]);
  const [ensembleEntries, setEnsembleEntries] = useState([]);
  const [addingEnsemble, setAddingEnsemble] = useState(Boolean(inviteCode));
  const [ensembleBusy, setEnsembleBusy] = useState(false);

  useEffect(() => {
    if (!registration?.id) return;
    postApi('/api/ensemble', { action: 'status', registrationId: registration.id })
      .then(data => setEnsembleEntries(data.entries))
      .catch(err => console.warn('Could not load ensembles:', err));
  }, [registration?.id]);

  // Throws Error(message) for the card to show.
  const runEnsemble = async (action, body = {}) => {
    const before = new Set(ensembleEntries.map(e => e.entryId));
    const data = await postApi('/api/ensemble', { action, registrationId: registration.id, ...body });
    setEnsembleEntries(data.entries);
    // Open whatever entry was just created.
    data.entries.filter(e => !before.has(e.entryId)).forEach(e => toggle(`e-${e.entryId}`, true));
    return data;
  };

  const forgetInvite = () => { try { localStorage.removeItem(INVITE_KEY); } catch { /* ignore */ } };

  const addEnsemble = async (action, body) => {
    setEnsembleBusy(true);
    try {
      await runEnsemble(action, body);
      setAddingEnsemble(false);
      if (action === 'join') forgetInvite();
    } finally {
      setEnsembleBusy(false);
    }
  };

  const savedSoloCount = (variations || []).length;
  useEffect(() => {
    if (readOnly || !registration?.id) return;
    let cancelled = false;
    postApi('/api/price-quote', { registrationId: registration.id })
      .then(data => {
        if (cancelled) return;
        setPrices(data.prices);
        setPaidInfo(data.paid);
      })
      .catch(err => console.warn('Could not load prices:', err));
    return () => { cancelled = true; };
  }, [readOnly, registration?.id, savedSoloCount, ensembleEntries.length]);


  const isAdult = isAdultDivision(ageDivision);
  const lockCategory = (data, division) => (
    division && !isAdultDivision(division) ? { ...data, performance_category: COMPETITIVE_CATEGORY } : data
  );

  /**
   * Age division + solo cards from the saved variations. Cards added on this
   * page but not saved yet are kept when the saved list refreshes.
   */
  useEffect(() => {
    const division = dancerInfo?.date_of_birth ? getAgeDivision(dancerInfo.date_of_birth) : '';
    setAgeDivision(division);

    const saved = (variations || []).map(v => ({
      number: v.variation_number,
      data: lockCategory(pickSoloFields(v), v.age_division || division),
      persisted: true,
      paid: Boolean(v.paid_payment_id),
    }));
    setSolos(prev => {
      const unsaved = prev.filter(s => !s.persisted && !saved.some(v => v.number === s.number));
      return [...saved, ...unsaved].sort((a, b) => a.number - b.number);
    });
  }, [dancerInfo, variations]);

  const errorPrefix = (number) => `solo${number}`;

  const handleChange = (number, field, value) => {
    setSolos(prev => prev.map(s => (s.number === number ? { ...s, data: { ...s.data, [field]: value } } : s)));
    const key = `${errorPrefix(number)}_${field}`;
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
  };

  const handleAddSolo = () => {
    const taken = solos.map(s => s.number);
    const number = [1, 2].find(n => !taken.includes(n));
    if (!number) return;
    setSolos(prev => [...prev, { number, data: lockCategory(EMPTY_SOLO, ageDivision), persisted: false, paid: false }]
      .sort((a, b) => a.number - b.number));
    toggle(number, true);
    setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 100);
  };

  const handleRemoveSolo = async (solo) => {
    const msg = solo.persisted
      ? 'Remove this solo? Any music you uploaded for it will also be removed.'
      : 'Remove this solo?';
    if (!window.confirm(msg)) return;

    if (solo.persisted && registration?.id) {
      try {
        setRemoving(true);
        await registrationService.deleteVariation(registration.id, solo.number);
        if (refreshRegistration) await refreshRegistration();
      } catch (err) {
        console.error('❌ Failed to remove solo:', err);
        alert(`Could not remove this solo: ${err.message}`);
        return;
      } finally {
        setRemoving(false);
      }
    }
    setSolos(prev => prev.filter(s => s.number !== solo.number));
    setErrors(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith(errorPrefix(solo.number)))));
  };

  /**
   * Music saves straight away with the card's current details, so an upload
   * isn't lost if the dancer leaves before "Save & Continue".
   */
  const handleMusicUpload = async (number, musicData) => {
    const solo = solos.find(s => s.number === number);
    handleChange(number, 'music_file_url', musicData.url);
    handleChange(number, 'music_file_name', musicData.fileName);
    if (!solo) return;

    const saved = await saveVariation(number, {
      age_division: ageDivision,
      ...toRow(solo.data),
      music_file_url: musicData.url,
      music_file_name: musicData.fileName,
      music_uploaded_at: new Date().toISOString(),
    });
    if (!saved) alert('Your music uploaded, but we could not attach it to this solo yet. Please click "Save & Continue".');
  };

  const validate = () => {
    const found = {};
    solos.forEach(({ number, data }) => {
      if (!data.performance_category) found[`${errorPrefix(number)}_performance_category`] = 'Performance category is required';
      if (!data.footwear) found[`${errorPrefix(number)}_footwear`] = 'Please choose pointe or soft shoes';
    });
    setErrors(found);
    // Open any card with a problem so the dancer can see it.
    solos.forEach(({ number }) => {
      if (Object.keys(found).some(k => k.startsWith(`${errorPrefix(number)}_`))) toggle(number, true);
    });
    if (solos.length === 0 && ensembleEntries.length === 0) {
      setSubmitError('Add at least one entry to continue.');
      return false;
    }
    return Object.keys(found).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // Only move on once every save has landed; saveVariation returns false on failure.
    for (const solo of solos) {
      const saved = await saveVariation(solo.number, { age_division: ageDivision, ...toRow(solo.data) });
      if (!saved) {
        setSubmitError("We couldn't save your changes. Please check your connection and try again — if it keeps happening, email info@prixducanada.com.");
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }
    goToNextStep();
  };

  return (
    <form onSubmit={handleSubmit}>
      {submitError && (
        <Alert color="red" mb="md">{submitError}</Alert>
      )}
      <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

      {!readOnly && (
        <Text size="sm" mb="md" style={{ textAlign: 'left', color: 'var(--soft-gray)' }}>
          Choose what you'll dance — up to {MAX_SOLOS} solos and 2 ensembles. Not sure of your piece, music or
          team yet? Add the entry and pay to hold your spot, then come back and finish the details any time
          until registration closes.
        </Text>
      )}

      {/* Fresh page: one clear choice */}
      {!readOnly && solos.length === 0 && ensembleEntries.length === 0 && !addingEnsemble ? (
        <Paper radius="md" p={{ base: 'md', sm: 'xl' }} mb="xl" style={{ background: 'var(--soft-cream)', textAlign: 'left' }}>
          <Title order={3} size="h4" mb={4} style={{ color: 'var(--charcoal)' }}>What will you dance?</Title>
          <Text size="sm" mb="lg" style={{ color: 'var(--soft-gray)' }}>Add at least one entry to continue.</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" maw={520}>
            <PrimaryButton type="button" leftSection={<IconPlus size={18} />} onClick={handleAddSolo}>
              Add a solo{prices ? ` · ${money(prices.solo)}` : ''}
            </PrimaryButton>
            <SecondaryButton type="button" leftSection={<IconPlus size={18} />} onClick={() => setAddingEnsemble(true)}>
              Add an ensemble{prices ? ` · ${money(prices.ensemble)}` : ''}
            </SecondaryButton>
          </SimpleGrid>
          {prices?.comboOpen && (
            <Text size="xs" mt="md" style={{ color: 'var(--deep-gold)' }}>
              <IconSparkles size={12} style={{ verticalAlign: 'middle' }} /> Solo + ensemble deal until {shortDate(prices.comboEndsAt)}:
              a solo + an ensemble for {money(prices.combo)} — the discount is applied automatically at checkout.
            </Text>
          )}
        </Paper>
      ) : (
        <>
          {/* Solos */}
          <SectionHeader
            title="Solos"
            count={solos.length}
            max={MAX_SOLOS}
            action={!readOnly && solos.length < MAX_SOLOS && (
              <AddButton onClick={handleAddSolo}>Add a solo{prices ? ` · ${money(prices.solo)}` : ''}</AddButton>
            )}
          />
          {solos.length === 0 && (
            <Text size="sm" color="dimmed" mb="md" style={{ textAlign: 'left' }}>No solos.</Text>
          )}
          {solos.map((solo, i) => (
            <VariationCard
              key={solo.number}
              variationNumber={solo.number}
              title={solos.length > 1 ? `Solo ${i + 1}` : 'Solo'}
              data={solo.data}
              onChange={(field, value) => handleChange(solo.number, field, value)}
              errors={errors}
              errorPrefix={errorPrefix(solo.number)}
              isAdult={isAdult}
              ageDivision={ageDivision}
              registrationId={registration?.id}
              onMusicUpload={(musicData) => handleMusicUpload(solo.number, musicData)}
              onRemove={!solo.paid && !removing && !readOnly ? () => handleRemoveSolo(solo) : null}
              paid={solo.paid}
              expanded={isOpen(solo)}
              onToggle={() => toggle(solo.number, !isOpen(solo))}
              footwearWarning={softShoeWarning({
                dateOfBirth: dancerInfo?.date_of_birth,
                footwear: solo.data.footwear,
                isAdultEntry: isAdult,
              })}
            />
          ))}

          {/* Ensembles */}
          <SectionHeader
            title="Ensembles"
            count={ensembleEntries.length}
            max={2}
            action={!readOnly && ensembleEntries.length < 2 && !addingEnsemble && (
              <AddButton onClick={() => setAddingEnsemble(true)}>
                Add an ensemble{prices ? ` · ${money(prices.ensemble)}` : ''}
              </AddButton>
            )}
          />
          {!readOnly && prices?.comboOpen && solos.length > ensembleEntries.length && ensembleEntries.length < 2 && (
            <Text size="sm" mb="sm" style={{ textAlign: 'left', color: 'var(--deep-gold)' }}>
              <IconSparkles size={14} style={{ verticalAlign: 'middle' }} /> Solo + ensemble deal until {shortDate(prices.comboEndsAt)}:
              a solo + an ensemble for {money(prices.combo)} — the discount is applied automatically at checkout.
            </Text>
          )}
          {ensembleEntries.length === 0 && !addingEnsemble && (
            <Text size="sm" color="dimmed" mb="md" style={{ textAlign: 'left' }}>
              Dancing in a group of 3–8? Each dancer registers and pays for their own spot.
            </Text>
          )}
          {ensembleEntries.map((entry, i) => (
            <EnsembleCard
              key={entry.entryId}
              entry={entry}
              index={i + 1}
              registrationId={registration?.id}
              onAction={runEnsemble}
              expanded={expanded[`e-${entry.entryId}`] ?? !entry.team}
              onToggle={() => toggle(`e-${entry.entryId}`, !(expanded[`e-${entry.entryId}`] ?? !entry.team))}
              readOnly={readOnly}
            />
          ))}
        </>
      )}

      {/* Add-an-ensemble form (from either the empty state or the section button) */}
      {!readOnly && addingEnsemble && ensembleEntries.length < 2 && (
        <Paper radius="md" p={{ base: 'sm', sm: 'lg' }} withBorder mb="md" style={{ borderTop: '4px solid var(--primary-gold)' }}>
          <Group justify="space-between" mb="md">
            <Title order={3} size="h5" style={{ color: 'var(--charcoal)' }}>Add an ensemble</Title>
            <Button size="xs" variant="subtle" color="gray" onClick={() => { setAddingEnsemble(false); forgetInvite(); }}>Cancel</Button>
          </Group>
          <EnsembleJoinForm
            initialCode={ensembleEntries.length === 0 ? inviteCode : ''}
            busy={ensembleBusy}
            onStart={(name, footwear) => addEnsemble('create', { name, footwear })}
            onJoin={(code, footwear) => addEnsemble('join', { code, footwear })}
            onLater={(footwear) => addEnsemble('joinLater', footwear ? { footwear } : {})}
          />
        </Paper>
      )}


      </fieldset>

      {!readOnly && prices && paidInfo && !paidInfo.registrationFee && (
        <>
          <Divider my="lg" />
          <Text size="sm" style={{ textAlign: 'left', color: 'var(--soft-gray)' }}>
            A one-time registration fee of <strong>{money(prices.registration)}</strong>
            {prices.earlyBird ? ` (early bird until ${shortDate(prices.earlyBirdEndsAt)}, then ${money(prices.registrationRegular)})` : ''} is
            added at checkout. You'll see your total on the next step.
          </Text>
        </>
      )}

      {/* Navigation Buttons */}
      <Group justify="space-between" mt="xl">
        <Button variant="subtle" onClick={goToPreviousStep} disabled={saving || removing}>
          Back
        </Button>
        {readOnly ? (
          <PrimaryButton type="button" onClick={() => navigate('/profile')}>
            Return to My Profile
          </PrimaryButton>
        ) : (
          <PrimaryButton type="submit" loading={saving || removing}>
            Save & Continue
          </PrimaryButton>
        )}
      </Group>
    </form>
  );
};

export default Step3Variations;
